import React from "react"; 
import ProductBanner from "../../component/productBanner";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons'
import UploadGrid from "../../component/UploadGrid";
import Cart from "../../component/Cart";
import axios from "axios"
import { Navigate } from "react-router-dom";
import { useSearchForCodeQuery, setCustomer} from "../../features/customer/photosSlice"
import { useSelector, useDispatch } from "react-redux"
import DragNdrop from "../../component/DragNDrop";

let BaseURL = process.env.REACT_APP_BASEURL
const imageMimeType = /image\/(png|jpg|jpeg)/i;

const photoPackage = {
    qty:1,
    name: "Santa Package",
    package: {name:"Santa Package"},
    photo: {id: "abcd123", path: "/QVB2212345/abcd123.png", url: "dropbox.com/image.png"},
    price: 4500
}

const Photos = () => {
    const { code, email, link} = useSelector((state) => state.admin)
    const [redirectTo, setRedirectTo] = React.useState()
    const {data : files, error, isLoading} = useSearchForCodeQuery(code)
    const [loadingCheckoutLink , setLoadingCheckoutLink] = React.useState(false)
    // Enhanced: upload queue state
    // Each item: { file, status, progress, previewUrl }
    const [queue, setQueue] = React.useState([]); // status: queued, uploading, uploaded, failed
    const [firstFile, SetFirstFile] = React.useState(true)

    // On mount, redirect if no code
    React.useEffect(()=>{
        if (code.length < 1){
            setRedirectTo('/admin')
        }
    },[])

    // Clean up preview URLs on unmount or when queue changes
    React.useEffect(() => {
        return () => {
            queue.forEach(q => {
                if (q.previewUrl) URL.revokeObjectURL(q.previewUrl);
            });
        }
    }, []);

    // Helper: update queue for a given file name
    const updateQueueItem = (fileName, update) => {
        setQueue(qs => qs.map(q =>
            q.file.name === fileName ? { ...q, ...update } : q
        ));
    };

    // Helper: remove file from queue by name
    const removeQueueItem = (fileName) => {
        setQueue(qs => {
            const toRemove = qs.find(q => q.file.name === fileName);
            if (toRemove && toRemove.previewUrl) {
                URL.revokeObjectURL(toRemove.previewUrl);
            }
            return qs.filter(q => q.file.name !== fileName);
        });
    };

    // Enhanced upload handler with per-file progress, status, retry
    const uploadFileHandler = async (uploadFiles) => {
        // Accept either FileList or array of Files
        let filesArray = Array.from(uploadFiles);
        // Mark as queued if not already in queue
        setQueue(prev => [
            ...prev,
            ...filesArray.filter(f => !prev.some(q => q.file.name === f.name)).map(f => ({
                file: f,
                status: "queued",
                progress: 0,
                previewUrl: URL.createObjectURL(f),
            }))
        ]);
    };

    // Effect: whenever queue has queued files, start uploading them
    React.useEffect(() => {
        const uploadNext = async () => {
            // Find first queued file
            const next = queue.find(q => q.status === "queued");
            if (!next) return;
            // Mark as uploading
            updateQueueItem(next.file.name, { status: "uploading", progress: 0 });
            try {
                // Get upload link from server
                const res = await axios.post(`${BaseURL}/public/files/uploadLink/${code}`, {
                    files: [next.file.name]
                });
                const fileInfo = res.data[0];
                let attempts = 0;
                let success = false;
                while (attempts < 3 && !success) {
                    attempts++;
                    try {
                        await axios.post(fileInfo.link, next.file, {
                            headers: { "content-type": "application/octet-stream" },
                            onUploadProgress: (progressEvent) => {
                                const percent = progressEvent.total
                                    ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
                                    : 0;
                                updateQueueItem(next.file.name, { progress: percent, status: "uploading" });
                            }
                        });
                        // Send email only for the first file
                        if (firstFile) {
                            axios.post(`${BaseURL}/public/files/admin/sendEmail`, {
                                code,
                                to: email,
                                link: `https://mpl.stardom.photos/${code}`,
                            });
                            SetFirstFile(false);
                        }
                        // Success: update status and cleanup preview
                        updateQueueItem(next.file.name, { status: "uploaded", progress: 100 });
                        // Cleanup preview after a short delay
                        // setTimeout(() => {
                        //     updateQueueItem(next.file.name, { previewUrl: null });
                        // }, 1000);
                        success = true;
                    } catch (err) {
                        if (attempts >= 3) {
                            updateQueueItem(next.file.name, { status: "failed", progress: 0 });
                        } else {
                            // Wait before retrying
                            await new Promise(res => setTimeout(res, 2000));
                        }
                    }
                }
            } catch (err) {
                updateQueueItem(next.file.name, { status: "failed", progress: 0 });
            }
        };
        // Only one upload at a time
        if (queue.some(q => q.status === "queued") && !queue.some(q => q.status === "uploading")) {
            uploadNext();
        }
    }, [queue, code, email, firstFile]);

    // Retry a failed file
    const handleRetry = (fileName) => {
        updateQueueItem(fileName, { status: "queued", progress: 0 });
    };

    // Retry all failed files
    const handleRetryAll = () => {
        setQueue(qs => qs.map(q =>
            q.status === "failed" ? { ...q, status: "queued", progress: 0 } : q
        ));
    };

    // Remove a file from queue
    const handleRemove = (fileName) => {
        removeQueueItem(fileName);
    };

    // Enhanced DragNDrop logic: filter duplicates, keep previews
    const handleFilesSelected = (newFiles) => {
        const arr = Array.from(newFiles);
        const filtered = arr.filter(
            f => !queue.some(q => q.file.name === f.name)
        );
        if (filtered.length > 0) {
            setQueue(prev => [
                ...prev,
                ...filtered.map(f => ({
                    file: f,
                    status: "queued",
                    progress: 0,
                    previewUrl: URL.createObjectURL(f),
                }))
            ]);
        }
    };

    // Batch summary panel
    const totalQueued = queue.filter(q => q.status === "queued").length;
    const totalUploading = queue.filter(q => q.status === "uploading").length;
    const totalUploaded = queue.filter(q => q.status === "uploaded").length;
    const totalFailed = queue.filter(q => q.status === "failed").length;

    // Status badge
    const StatusBadge = ({ status }) => {
        let color, text;
        switch (status) {
            case "queued": color = "#888"; text = "Queued"; break;
            case "uploading": color = "#3498db"; text = "Uploading"; break;
            case "uploaded": color = "#27ae60"; text = "Uploaded"; break;
            case "failed": color = "#e74c3c"; text = "Failed"; break;
            default: color = "#aaa"; text = status;
        }
        return (
            <span style={{
                background: color,
                color: "white",
                borderRadius: 6,
                padding: "2px 7px",
                fontSize: "0.8em",
                marginLeft: 6,
                fontWeight: 600
            }}>{text}</span>
        );
    };

    // Modal for image preview
    const [modalImage, setModalImage] = React.useState(null);

    const FileThumb = ({ q, onRetry, onRemove }) => (
        <div style={{
            position: "relative",
            display: "inline-block",
            margin: 10,
            width: 180,
            height: 200, // increased for filename at bottom
            background: "#f4f4f4",
            borderRadius: 8,
            overflow: "hidden",
            boxShadow: "0 1px 4px #0001"
        }}>
            <div style={{ width: "100%", height: 160, position: "relative", cursor: q.previewUrl ? "pointer" : "default" }}
                onClick={() => q.previewUrl && setModalImage(q.previewUrl)}>
                {q.previewUrl ?
                    <img src={q.previewUrl}
                        alt={q.file.name}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    :
                    q.status === "uploading" ?
                    <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#bbb" }}>
                        <FontAwesomeIcon icon={faCircleNotch} spin />
                    </div>
                    :
                    <img src={URL.createObjectURL(q.file)}
                        alt={q.file.name}
                        style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.6 }} />
                }
                {/* Overlay for status badge */}
                <div style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    background: "rgba(0,0,0,0.5)",
                    color: "white",
                    fontSize: 14,
                    padding: "4px 6px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}>
                    <StatusBadge status={q.status} />
                </div>
                {/* Progress bar */}
                {q.status === "uploading" &&
                    <div style={{
                        position: "absolute",
                        bottom: 0, left: 0, width: "100%",
                        height: 4, background: "#eee"
                    }}>
                        <div style={{
                            width: `${q.progress}%`,
                            height: "100%",
                            background: "#3498db",
                            transition: "width 0.2s"
                        }} />
                    </div>
                }
                {/* Retry/Remove buttons for failed */}
                {q.status === "failed" &&
                    <div style={{
                        position: "absolute",
                        top: 0, left: 0, width: "100%",
                        height: "100%",
                        background: "rgba(231,76,60,0.7)",
                        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center"
                    }}>
                        <button onClick={e => { e.stopPropagation(); onRetry(q.file.name); }} style={{
                            background: "#fff", color: "#e74c3c", border: 0, borderRadius: 4, padding: "2px 7px", margin: 2, cursor: "pointer", fontWeight: 700
                        }}>Retry</button>
                        <button onClick={e => { e.stopPropagation(); onRemove(q.file.name); }} style={{
                            background: "#fff", color: "#444", border: 0, borderRadius: 4, padding: "2px 7px", margin: 2, cursor: "pointer"
                        }}>Remove</button>
                    </div>
                }
            </div>
            {/* File name at bottom */}
            <div style={{
                width: "100%",
                height: 30,
                background: "#222",
                color: "#fff",
                fontSize: 14,
                textAlign: "center",
                padding: "4px 6px",
                whiteSpace: "normal",
                overflow: "hidden",
                textOverflow: "ellipsis",
                position: "absolute",
                left: 0,
                bottom: 0,
                zIndex: 2
            }}>
                {q.file.name}
            </div>
        </div>
    );

    // Modal overlay for image preview
    const ModalPreview = ({ src, onClose }) => {
        React.useEffect(() => {
            const handler = e => {
                if (e.key === "Escape") onClose();
            };
            window.addEventListener("keydown", handler);
            return () => window.removeEventListener("keydown", handler);
        }, [onClose]);
        return (
            <div
                onClick={onClose}
                style={{
                    position: "fixed",
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: "rgba(0,0,0,0.85)",
                    zIndex: 10000,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                }}>
                <div style={{ position: "relative" }} onClick={e => e.stopPropagation()}>
                    <img src={src} alt="Preview" style={{
                        maxWidth: "90vw",
                        maxHeight: "80vh",
                        borderRadius: 8,
                        boxShadow: "0 2px 24px #000a"
                    }} />
                    <button
                        onClick={onClose}
                        style={{
                            position: "absolute",
                            top: 8, right: 8,
                            background: "rgba(0,0,0,0.7)",
                            color: "#fff",
                            border: 0,
                            borderRadius: "50%",
                            width: 32, height: 32,
                            fontSize: 20,
                            cursor: "pointer"
                        }}
                        aria-label="Close preview"
                    >&times;</button>
                </div>
            </div>
        );
    };

    return <>
        {modalImage && <ModalPreview src={modalImage} onClose={() => setModalImage(null)} />}
        {redirectTo && <Navigate to={redirectTo}/>}
        <div style={{background: "blueviolet"}} className={"flexContainer"}>
            <div><h1>ADMIN PAGE </h1></div>
            <div><h1>CODE: {code}</h1></div>
            <div><h1>EMAIL: {email}</h1></div>
        </div>
        {/* Drag & Drop using DragNdrop component */}
        <DragNdrop
            onFilesSelected={handleFilesSelected}
            uploadFileHandler={uploadFileHandler}
            style={{
                width: "100%",
                height: "400px",
                border: "2px dashed #3498db",
                borderRadius: 8,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#f0f8ff",
                cursor: "pointer",
                transition: "background-color 0.2s, border-color 0.2s",
                marginBottom: "20px",
                position: "relative",
            }}
        >
            <div style={{
                position: "absolute",
                textAlign: "center",
                color: "#3498db",
                fontSize: "1.2em",
                pointerEvents: "none",
                width: "100%"
            }}>
                Drag & drop files here, or click to select
            </div>
        </DragNdrop>
        {/* Batch summary panel */}
        <div style={{
            background: "#f8f8f8",
            margin: "20px 0",
            borderRadius: 8,
            padding: "10px 20px",
            boxShadow: "0 1px 6px #0001",
            display: "flex",
            alignItems: "center",
            gap: 30,
            flexWrap: "wrap"
        }}>
            <span><strong>Queued:</strong> {totalQueued}</span>
            <span><strong>Uploading:</strong> {totalUploading}</span>
            <span><strong>Uploaded:</strong> {totalUploaded}</span>
            <span><strong>Failed:</strong> {totalFailed}</span>
            {totalFailed > 0 &&
                <button style={{
                    background: "#e74c3c",
                    color: "white",
                    border: 0,
                    borderRadius: 5,
                    padding: "5px 12px",
                    fontWeight: 600,
                    cursor: "pointer"
                }} onClick={handleRetryAll}>Retry All Failed</button>
            }
        </div>
        {/* Upload grid */}
        <div style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            justifyContent: "space-between"
        }}>
            {queue.map((q, i) =>
                <FileThumb
                    key={q.file.name}
                    q={q}
                    onRetry={handleRetry}
                    onRemove={handleRemove}
                />
            )}
        </div>
    </>
}

export default Photos